import {
    CrossIcon,
    HamburgerIcon
} from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";
import { Link } from "react-router-dom";

import { SolomonLogo } from "@solomon-apps/shared";

import type { YaasTopBarUser } from "./YaasTopBar";

interface YaasTopBarClassicProps {
    /**
     * Fallback label shown on the left when `user` isn't loaded yet.
     * Once `user` is present the left label is `user.orgName`.
     */
    appLabel: string;
    user?: YaasTopBarUser;
    /** Accepted for API compatibility with the Layout's `renderTopBar`
     *  pass-through. No longer drives the logo block width — the logo is a
     *  header element and keeps its size regardless of the nav-rail collapse. */
    sidebarCollapsed?: boolean;
    /**
     * Mobile only — toggles the off-canvas nav drawer. When provided (the
     * shell passes it only on mobile), a hamburger renders at the END of the
     * bar and the org/issuer labels are hidden (they move into the drawer).
     */
    onMenuClick?: () => void;
    /** Whether the mobile drawer is currently open (drives the icon). */
    menuOpen?: boolean;
    /**
     * When provided, the logo becomes a link to this path (e.g. the client
     * console home). Omitted for consoles where the logo stays inert.
     */
    logoTo?: string;
}

/**
 * Tai's May-8 (`7da52c0f`) client-console top bar, restored client-local
 * so the shared `YaasTopBar` (and the issuer console that depends on it)
 * stays on the current design. Logo block + org name (middle) +
 * "Issuer: {name}" (right). Used by the design-restore `*-old` routes via
 * the Layout's `renderTopBar` override.
 */
export function YaasTopBarClassic({
    appLabel,
    user,
    onMenuClick,
    menuOpen = false,
    logoTo
}: YaasTopBarClassicProps): React.ReactElement {
    const leftLabel = user?.orgName ?? appLabel;

    return (
        <header className="flex items-stretch shrink-0 z-10 bg-surface-ground border-b border-line-base">
            {/* Logo is a header element, not part of the nav rail. Fixed width
                on desktop (w-52) so it aligns with the rail and never clips on
                collapse; natural width on mobile, where there's no rail. The
                lockup stays h-6 (~123px) at every width: brand/lockup has a
                120px minimum. When `logoTo` is set the logo links home;
                otherwise it stays inert. */}
            <div className="flex items-center px-4 py-3 shrink-0 w-auto md:w-52">
                {logoTo ? (
                    <Link
                        to={logoTo}
                        aria-label="Home"
                        className="inline-flex items-center transition-opacity hover:opacity-80"
                    >
                        <SolomonLogo className="h-6 w-auto" />
                    </Link>
                ) : (
                    <SolomonLogo className="h-6 w-auto" />
                )}
            </div>

            {/* Org name — desktop only. On mobile it moves into the drawer
                footer (see YaasSidebar), leaving the bar to the logo + menu. */}
            <div className="hidden md:flex items-center px-4 py-3">
                <Text variant="h3" className="text-foreground-base">
                    {leftLabel}
                </Text>
            </div>

            <div className="flex-1" />

            {user?.issuerName && (
                <div className="hidden md:flex items-center px-4 py-3">
                    <Text variant="h3" className="text-foreground-muted">
                        Distributor:{" "}
                        <span className="text-foreground-base">
                            {user.issuerName}
                        </span>
                    </Text>
                </div>
            )}

            {/* Mobile hamburger, top-right. Hidden at md+ (in-flow rail). */}
            {onMenuClick && (
                <button
                    type="button"
                    onClick={onMenuClick}
                    aria-label={
                        menuOpen
                            ? "Close navigation menu"
                            : "Open navigation menu"
                    }
                    aria-expanded={menuOpen}
                    className="flex md:hidden w-11 shrink-0 items-center justify-center text-foreground-muted transition-colors hover:text-foreground-base"
                >
                    {menuOpen ? (
                        <CrossIcon className="size-5" />
                    ) : (
                        <HamburgerIcon className="size-5" />
                    )}
                </button>
            )}
        </header>
    );
}
